import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import logoMatches from "../../static/Home/logo_matches.webp";

const statusConfig = {
  live: {
    label: "LIVE",
    bg: "rgba(211, 47, 47, 0.12)",
    color: "#d32f2f",
  },
  scheduled: {
    label: "SCHEDULED",
    bg: "rgba(8, 112, 162, 0.12)",
    color: "#0870A2",
  },
  finished: {
    label: "FINISHED",
    bg: "rgba(25, 118, 210, 0.12)",
    color: "#1976d2",
  },
  cancelled: {
    label: "CANCELLED",
    bg: "rgba(97, 97, 97, 0.14)",
    color: "#616161",
  },
};

export default function NextMatchesSection() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const pollTimeoutRef = useRef(null);
  const isMountedRef = useRef(false);

  const formatDateLabel = (date, time) => {
    const today = new Date();
    const matchDate = new Date(`${date}T12:00:00`);

    const isToday =
      today.getFullYear() === matchDate.getFullYear() &&
      today.getMonth() === matchDate.getMonth() &&
      today.getDate() === matchDate.getDate();

    if (isToday) return `TODAY · ${time}`;

    return `${matchDate.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
    })} · ${time}`;
  };

  const getStatusMeta = (status) => {
    return statusConfig[status] || statusConfig.scheduled;
  };

  const clearScheduledPoll = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const scheduleNextPoll = useCallback((seconds) => {
    clearScheduledPoll();

    const delay = Number(seconds);

    if (!Number.isFinite(delay) || delay <= 0) {
      return;
    }

    pollTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        loadHomeState();
      }
    }, delay * 1000);
  }, []);

  const loadHomeState = useCallback(async () => {
    try {
      const res = await fetch("/matches/home-next-state/");
      const data = await res.json();

      if (!isMountedRef.current) return;

      const safeMatches = Array.isArray(data?.matches) ? data.matches : [];
      setMatches(safeMatches);
      scheduleNextPoll(data?.next_check_in_seconds);
    } catch {
      if (!isMountedRef.current) return;
      setMatches([]);
      scheduleNextPoll(30);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [scheduleNextPoll]);

  useEffect(() => {
    isMountedRef.current = true;

    loadHomeState();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadHomeState();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      clearScheduledPoll();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadHomeState]);

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: "primary.main",
        py: { xs: 5, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            width: "100%",
            maxWidth: 1040,
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1.7rem", md: "2.2rem" },
              fontWeight: 700,
              color: "#fff",
              mb: 0.8,
            }}
          >
            Featured Matches
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.85)",
              mb: 3.5,
            }}
          >
            Live scores and upcoming games from the tournament
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress sx={{ color: "#fff" }} />
            </Box>
          ) : matches.length ? (
            <Stack spacing={2} alignItems="center">
              {matches.map((match) => {
                const statusMeta = getStatusMeta(match.status);
                const isLive = match.status === "live";
                const isScheduled = match.status === "scheduled";

                return (
                  <Card
                    key={match.id}
                    elevation={0}
                    sx={{
                      width: "100%",
                      maxWidth: 940,
                      borderRadius: "22px",
                      px: { xs: 1.4, sm: 2.2, md: 3 },
                      py: { xs: 1.8, sm: 2.2, md: 2.6 },
                      boxShadow: "0 18px 40px rgba(0,0,0,0.10)",
                    }}
                  >
                    <Stack spacing={{ xs: 1.4, md: 1.8 }}>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.8,
                            px: 1.3,
                            py: 0.55,
                            borderRadius: "999px",
                            backgroundColor: statusMeta.bg,
                            color: statusMeta.color,
                            fontWeight: 800,
                            fontSize: { xs: ".7rem", sm: ".75rem" },
                            lineHeight: 1,
                          }}
                        >
                          {isLive && (
                            <Box
                              sx={{
                                width: 9,
                                height: 9,
                                borderRadius: "50%",
                                backgroundColor: "#d32f2f",
                                boxShadow: "0 0 0 4px rgba(211,47,47,0.16)",
                              }}
                            />
                          )}
                          {statusMeta.label}
                        </Box>
                      </Box>

                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={{ xs: 1, sm: 1.8, md: 2.4 }}
                        sx={{ width: "100%" }}
                      >
                        <Stack
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            flex: "1 1 0",
                            minWidth: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={match.team_local_logo}
                            alt={match.team_local}
                            sx={{
                              width: { xs: 52, sm: 74, md: 88 },
                              height: { xs: 52, sm: 74, md: 88 },
                              objectFit: "contain",
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: ".86rem", sm: "1rem", md: "1.08rem" },
                              textAlign: "center",
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                            }}
                          >
                            {match.team_local}
                          </Typography>
                        </Stack>

                        <Stack
                          spacing={{ xs: 1, sm: 1.2 }}
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            flexShrink: 0,
                            minWidth: { xs: 148, sm: 220, md: 260 },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={{ xs: 0.6, sm: 1.1, md: 1.3 }}
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Box
                              sx={{
                                minWidth: { xs: 46, sm: 72, md: 84 },
                                height: { xs: 52, sm: 74, md: 84 },
                                px: { xs: 0.8, sm: 1.2 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "16px",
                                background: "linear-gradient(180deg, #ffffff 0%, #f5f8fb 100%)",
                                border: "1px solid rgba(8,112,162,0.14)",
                                boxShadow: "0 10px 24px rgba(8,112,162,0.08)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 900,
                                  fontSize: { xs: "1.8rem", sm: "2.7rem", md: "3.1rem" },
                                  color: "primary.main",
                                  lineHeight: 1,
                                }}
                              >
                                {isScheduled ? "-" : (match.score_local ?? 0)}
                              </Typography>
                            </Box>

                            <Box
                              component="img"
                              src={logoMatches}
                              alt="vs"
                              sx={{
                                width: { xs: 24, sm: 34, md: 38 },
                                height: { xs: 24, sm: 34, md: 38 },
                                objectFit: "contain",
                                flexShrink: 0,
                              }}
                            />

                            <Box
                              sx={{
                                minWidth: { xs: 46, sm: 72, md: 84 },
                                height: { xs: 52, sm: 74, md: 84 },
                                px: { xs: 0.8, sm: 1.2 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "16px",
                                background: "linear-gradient(180deg, #ffffff 0%, #f5f8fb 100%)",
                                border: "1px solid rgba(8,112,162,0.14)",
                                boxShadow: "0 10px 24px rgba(8,112,162,0.08)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 900,
                                  fontSize: { xs: "1.8rem", sm: "2.7rem", md: "3.1rem" },
                                  color: "primary.main",
                                  lineHeight: 1,
                                }}
                              >
                                {isScheduled ? "-" : (match.score_visitor ?? 0)}
                              </Typography>
                            </Box>
                          </Stack>

                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: ".76rem", sm: ".86rem" },
                              color: "text.secondary",
                              letterSpacing: ".04em",
                            }}
                          >
                            {match.category}
                          </Typography>
                        </Stack>

                        <Stack
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            flex: "1 1 0",
                            minWidth: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={match.team_visitor_logo}
                            alt={match.team_visitor}
                            sx={{
                              width: { xs: 52, sm: 74, md: 88 },
                              height: { xs: 52, sm: 74, md: 88 },
                              objectFit: "contain",
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: ".86rem", sm: "1rem", md: "1.08rem" },
                              textAlign: "center",
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                            }}
                          >
                            {match.team_visitor}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack spacing={0.55} alignItems="center" justifyContent="center">
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: { xs: ".82rem", sm: ".9rem" },
                            color: "text.secondary",
                            fontWeight: 700,
                            lineHeight: 1.2,
                          }}
                        >
                          {formatDateLabel(match.date, match.time)} · {match.venue}
                        </Typography>
                      </Stack>

                      {isLive && (
                        <Box sx={{ display: "flex", justifyContent: "center", pt: 0.4 }}>
                          <Button
                            variant="contained"
                            onClick={() => navigate(`/partido/${match.id}`)}
                            sx={{
                              minWidth: { xs: 170, sm: 190 },
                              height: 46,
                              backgroundColor: "primary.main",
                              color: "#fff",
                              fontWeight: 800,
                              textTransform: "none",
                              borderRadius: "12px",
                              px: 3,
                              boxShadow: "0 10px 24px rgba(8,112,162,0.24)",
                              transition: "transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease",
                              "&:hover": {
                                backgroundColor: "primary.dark",
                                transform: "scale(1.06)",
                                boxShadow: "0 14px 30px rgba(8,112,162,0.30)",
                              },
                            }}
                          >
                            Watch Live
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 2,
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                No featured matches available right now
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={() => navigate("/calendario-de-partidos")}
              sx={{
                mt: 3.2,
                backgroundColor: "#fff",
                color: "primary.main",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "12px",
                px: 3.2,
                height: 46,
                transition: "transform 0.25s ease, background-color 0.25s ease",
                "&:hover": {
                  backgroundColor: "#f4f4f4",
                  transform: "scale(1.08)",
                },
              }}
            >
              View Full Schedule
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}